import { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import { dangerousGoodsOptions, dangerousGoodsRegulations, holdAtLocationType } from '../../utils/dropdownOptions';
import axios from 'axios';
import { LoadingSpinner } from '../LoadingSpinner';
import Col from 'react-bootstrap/Col';
import { useNotifications } from '../../contexts/NotificationsContext';

export const SpecialServicesDetailsModal = ({ formik, show, handleClose, serviceType, serviceCode, formData, setFormData, isReturnShipmentTab }) => {
    const { addNotification } = useNotifications();
    const [locationData, setLocationData] = useState([]);
    const [locationIds, setLocationIds] = useState([]);
    const [locationDataLoading, setLocationDataLoading] = useState(false);
    const [locationDistance, setLocationDistance] = useState(20);
    const [locationMaxResults, setLocationMaxResults] = useState(10);
    const [showFilters, setShowFilters] = useState(false);
    const values = isReturnShipmentTab ? formik.values.returnData : formik.values;
    const specialServicesField = isReturnShipmentTab ? 'returnData.specialServices' : 'specialServices';

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        if (type === 'radio') {
            setFormData({ ...formData, [name]: value === 'yes' ? true : false });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    }

    const handleLocationIdChange = (e) => {
        const { name, value } = e.target;
        const IdFound = locationIds.includes(value);
        if (!IdFound) {
            return setFormData({ ...formData, [name]: value });
        }
        const locationObj = locationData.find((i) => i.locationId === value);
        const { contact, address } = locationObj.contactAndAddress;
        setFormData({
            ...formData,
            [name]: value,
            personName: contact.personName || '',
            email: contact.emailAddress || '',
            phoneNumber: contact.phoneNumber || '',
            phoneExtension: contact.phoneExtension || '',
            companyName: contact.companyName || '',
            country: address.countryCode,
            address1: address.streetLines[0],
            address2: address.streetLines[1] || '',
            address3: address.streetLines[2] || '',
            postalCode: address.postalCode,
            stateProvince: address.stateOrProvinceCode,
            city: address.city
        })
    }

    const handleDangerousGoodsOptionsChange = (e) => {
        const { checked, value } = e.target;
        if (checked) {
            const optionsArray = (formData.options && formData.options.length) ? [...formData.options] : [];
            optionsArray.push(value);
            setFormData({ ...formData, options: optionsArray });
        } else {
            const updatedOptionsArray = formData.options.filter((i) => i !== value)
            setFormData({ ...formData, options: updatedOptionsArray });
        }
    }

    const handleModalCancel = () => {
        const updatedSpecialServices = values.specialServices.map((item) => (
            item.code === serviceCode ? { ...item, selected: false } : item
        ));
        formik.setFieldValue(specialServicesField, updatedSpecialServices);
        setFormData({});
        handleClose();
    }

    const handleSubmit = () => {
        // TODO - form field validation?
        const updatedSpecialServices = values.specialServices.map((item) => (
            item.code === serviceCode ? { ...item, details: formData, selected: true } : item // manually setting selected here for the view/edit button in the options tab
        ));
        formik.setFieldValue(specialServicesField, updatedSpecialServices);
        setFormData({});
        handleClose();
    }

    const getLocations = async () => {
        setLocationDataLoading(true);
        const url = `${process.env.REACT_APP_BASE_URL}/fedex/locations`;
        const reqBody = {
            distance: {
                units: 'MI',
                value: locationDistance
            },
            maxResults: locationMaxResults,
            city: values.city,
            stateProvince: values.stateProvince,
            postalCode: values.postalCode,
            country: values.country
        }

        try {
            const result = await axios.post(url, reqBody);
            const data = result.data.data;
            const fetchedLocationIds = data.output.locationDetailList.map((i) => i.locationId);
            setLocationData(data.output.locationDetailList);
            setLocationIds(fetchedLocationIds);
            setLocationDataLoading(false);
        } catch (err) {
            setLocationDataLoading(false);
            const errDetails = err.response.data.details.errors && err.response.data.details.errors.length > 0 ? err.response.data.details.errors[0].code : '';
            addNotification('Error getting location info.', errDetails || err.message, 'danger');
            console.log('Error fetching location info: ' + err);
        }
    }

    const handleLocationIdFocus = () => {
        if (locationData.length === 0) {
            getLocations();
        }
    }

    const clearForm = () => {
        if (formData.options) {
            setFormData({ options: [] });
        } else {
            setFormData({});
        }
    }

    let title;
    let jsx;
    switch (serviceType) {
        case 'DRY_ICE':
            title = 'Dry Ice Details';
            jsx = <>
                <Form.Group className='form-group'>
                    <Form.Label>Weight</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type='number'
                            onWheel={(e) => e.target.blur()}
                            name='value'
                            value={formData.value || ''}
                            onChange={handleInputChange}
                        />
                        <Form.Select
                            name='units'
                            value={formData.units || ''}
                            onChange={handleInputChange}
                        >
                            <option value=''>Select an option</option>
                            <option value='LB'>lbs</option>
                            <option value='KG'>kgs</option>
                        </Form.Select>
                    </InputGroup>
                </Form.Group>
                <p>Note: The total weight should include the dry ice weight.</p>
            </>
            break;
        case 'ALCOHOL':
            title = 'Alcohol details';
            jsx = <>
                <Form.Group className='form-group'>
                    <Form.Label>Recipient type</Form.Label>
                    <Form.Select
                        name='alcoholRecipientType'
                        value={formData.alcoholRecipientType || ''}
                        onChange={handleInputChange}
                    >
                        <option value=''>Select an option</option>
                        <option value='LICENSEE'>Licensee</option>
                        <option value='CONSUMER'>Consumer</option>
                    </Form.Select>
                </Form.Group>
            </>
            break;
        case 'BATTERY':
            title = 'Battery details';
            jsx = <>
                <strong>The following Section II type batteries/cells are valid here:</strong>
                <ul>
                    <li>Ion packed with equipment (UN3481)</li>
                    <li>Ion contained in equipment (UN3481)</li>
                    <li>Metal packed with equipment (UN3091)</li>
                    <li>Metal contained in equipment (UN3091)</li>
                </ul>
                <Form.Group className='form-group'>
                    <Form.Label>Battery packing type</Form.Label>
                    <Form.Select
                        name='batteryPackingType'
                        value={formData.batteryPackingType || ''}
                        onChange={handleInputChange}
                    >
                        <option value=''>Select an option</option>
                        <option value='CONTAINED_IN_EQUIPMENT'>Contained in equipment</option>
                        <option value='PACKED_WITH_EQUIPMENT'>Packed with equipment</option>
                    </Form.Select>
                </Form.Group>
                <Form.Group className='form-group'>
                    <Form.Label>Batery regulatory type</Form.Label>
                    <Form.Control
                        disabled
                        type='text'
                        name='batteryRegulatoryType'
                        value={formData.batteryRegulatoryType || ''}
                    />
                </Form.Group>
                <Form.Group className='form-group'>
                    <Form.Label>Battery material type</Form.Label>
                    <Form.Select
                        name='batteryMaterialType'
                        value={formData.batteryMaterialType || ''}
                        onChange={handleInputChange}
                    >
                        <option value=''>Select an option</option>
                        <option value='LITHIUM_METAL'>Lithium metal</option>
                        <option value='LITHIUM_ION'>Lithium ion</option>
                    </Form.Select>
                </Form.Group>
                <p>Note: All Section I lithium batteries and cells, and all UN3480 and UN3090 batteries and cells must be entered as Dangerous Goods under Special Services.</p>
            </>
            break;
        case 'HOLD_AT_LOCATION':
            title = 'Hold at location details';
            jsx = <>
                <Form.Group className='form-group'>
                    <Form.Label>Location ID</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type='text'
                            list='locationIds'
                            name='locationId'
                            value={formData.locationId || ''}
                            onChange={handleLocationIdChange}
                            onFocus={handleLocationIdFocus}
                        />
                        <datalist id='locationIds'>
                            {
                                locationIds.map((i) => (
                                    <option key={i} value={i}>{i}</option>
                                ))
                            }
                        </datalist>
                        <Button onClick={getLocations} variant='secondary'>{locationDataLoading ? <LoadingSpinner /> : 'Reload'}</Button>
                    </InputGroup>
                    <Button className='link-btn' onClick={() => setShowFilters(!showFilters)}>Show filters</Button>
                </Form.Group>
                {showFilters &&
                    <Row className='form-group'>
                        <Form.Group as={Col}>
                            <Form.Label>Max distance</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type='number'
                                    onWheel={(e) => e.target.blur()}
                                    value={locationDistance}
                                    onChange={(e) => setLocationDistance(e.target.value)}
                                />
                                <InputGroup.Text>Miles</InputGroup.Text>
                            </InputGroup>
                        </Form.Group>
                        <Form.Group as={Col}>
                            <Form.Label>Max results</Form.Label>
                            <Form.Control
                                type='number'
                                onWheel={(e) => e.target.blur()}
                                value={locationMaxResults}
                                onChange={(e) => setLocationMaxResults(e.target.value)}
                            />
                        </Form.Group>
                    </Row>
                }
                <fieldset>
                    <legend>Contact information</legend>
                    <Form.Group className='form-group'>
                        <Form.Label>Contact name</Form.Label>
                        <Form.Control
                            type='text'
                            name='personName'
                            value={formData.personName || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Email address</Form.Label>
                        <Form.Control
                            type='text'
                            name='email'
                            value={formData.email || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Telephone</Form.Label>
                        <Form.Control
                            type='text'
                            name='phoneNumber'
                            value={formData.phoneNumber || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Ext</Form.Label>
                        <Form.Control
                            type='text'
                            name='phoneExtension'
                            value={formData.phoneExtension || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Company name</Form.Label>
                        <Form.Control
                            type='text'
                            name='companyName'
                            value={formData.companyName || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                </fieldset>
                <fieldset>
                    <legend>Fedex address where the shipment should be held</legend>
                    <Form.Group className='form-group'>
                        <Form.Label>Country <small>(2 letter code)</small></Form.Label>
                        <Form.Control
                            type='text'
                            name='country'
                            value={formData.country || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Address 1</Form.Label>
                        <Form.Control
                            type='text'
                            name='address1'
                            value={formData.address1 || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Address 2</Form.Label>
                        <Form.Control
                            type='text'
                            name='address2'
                            value={formData.address2 || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Address 3</Form.Label>
                        <Form.Control
                            type='text'
                            name='address3'
                            value={formData.address3 || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Postal code</Form.Label>
                        <Form.Control
                            type='text'
                            name='postalCode'
                            value={formData.postalCode || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>State/Province <small>(2 letter code)</small></Form.Label>
                        <Form.Control
                            type='text'
                            name='stateProvince'
                            value={formData.stateProvince || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>City</Form.Label>
                        <Form.Control
                            type='text'
                            list='holdAtLocationCities'
                            name='city'
                            value={formData.city || ''}
                            onChange={handleInputChange}
                        />
                        <datalist id='holdAtLocationCities'>
                            <option value='option1'>option1</option>
                            <option value='option2'>option2</option>
                        </datalist>
                    </Form.Group>
                    <Form.Group className='form-group'>
                        <Form.Label>Location type</Form.Label>
                        <Form.Select
                            name='locationType'
                            value={formData.locationType || ''}
                            onChange={handleInputChange}
                        >
                            <option value=''>Select an option</option>
                            {
                                holdAtLocationType.map((i) => (
                                    <option key={i.value} value={i.value}>{i.key}</option>
                                ))
                            }
                        </Form.Select>
                    </Form.Group>
                </fieldset>
            </>
            break;
        case 'DANGEROUS_GOODS':
            title = 'Dangerous goods details'
            jsx = <>
                <Form.Group className='form-group'>
                    <Form.Label>Aircraft only</Form.Label>
                    <br />
                    <Form.Check
                        inline
                        type='radio'
                        label='Yes'
                        onChange={handleInputChange}
                        value='yes'
                        checked={formData.cargoAircraftOnly === true}
                        name='cargoAircraftOnly'
                    />
                    <Form.Check
                        inline
                        type='radio'
                        label='No'
                        onChange={handleInputChange}
                        value='no'
                        checked={formData.cargoAircraftOnly === false}
                        name='cargoAircraftOnly'
                    />
                </Form.Group>
                <Form.Group className='form-group'>
                    <Form.Label>Regulation</Form.Label>
                    <Form.Select
                        name='regulation'
                        value={formData.regulation || ''}
                        onChange={handleInputChange}
                    >
                        <option value=''>Select an option</option>
                        {
                            dangerousGoodsRegulations.map((i) => (
                                <option key={i} value={i}>{i}</option>
                            ))
                        }
                    </Form.Select>
                </Form.Group>
                <Form.Group className='form-group'>
                    <Form.Label>Accessibility <small>(Accessibility is only required for IATA controlled DG shipments.)</small></Form.Label>
                    <Form.Select
                        name='accessibility'
                        value={formData.accessibility || ''}
                        onChange={handleInputChange}
                    >
                        <option value=''>N/A</option>
                        <option value='ACCESSIBLE'>Accessible</option>
                        <option value='INACCESSIBLE'>Inaccessible</option>
                    </Form.Select>
                </Form.Group>
                <Form.Group>
                    <Form.Label>Options</Form.Label>
                    {
                        dangerousGoodsOptions.map((i) => (
                            <Form.Check
                                key={i.value}
                                type='checkbox'
                                label={i.key}
                                onChange={handleDangerousGoodsOptionsChange}
                                value={i.value}
                                checked={formData.options && formData.options.includes(i.value)}
                                name='options'
                            />
                        ))
                    }
                </Form.Group>
            </>
            break;
        case 'HOME_DELIVERY_PREMIUM':
            title = 'Home delivery premium details';
            jsx = <>
                <Row className='form-group'>
                    <Form.Group as={Col}>
                        <Form.Label>Phone number <small>(Format: 123-456-7890)</small></Form.Label>

                        <Form.Control
                            type='tel'
                            name='phoneNumber'
                            value={formData.phoneNumber || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group as={Col}>
                        <Form.Label>Ext</Form.Label>
                        <Form.Control
                            type='text'
                            name='extension'
                            value={formData.extension || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                    <Form.Group as={Col}>
                        <Form.Label>Personal ID number</Form.Label>
                        <Form.Control
                            type='text'
                            name='personalIdentificationNumber'
                            value={formData.personalIdentificationNumber || ''}
                            onChange={handleInputChange}
                        />
                    </Form.Group>
                </Row>
                <Form.Group className='form-group'>
                    <Form.Label>Delivery date</Form.Label>
                    <Form.Control
                        type='date'
                        name='deliveryDate'
                        value={formData.deliveryDate || ''}
                        onChange={handleInputChange}
                    />
                </Form.Group>
                <Form.Group className='form-group'>
                    <Form.Label>Home delivery premium type</Form.Label>
                    <Form.Select
                        name='homedeliveryPremiumType'
                        value={formData.homedeliveryPremiumType || ''}
                        onChange={handleInputChange}
                    >
                        <option value=''>Select an option</option>
                        <option value='APPOINTMENT'>Appointment</option>
                        <option value='DATE_CERTAIN'>Date certain</option>
                        <option value='EVENING'>Evening</option>
                    </Form.Select>
                </Form.Group>
            </>
            break;
        default:
            title = 'Something went wrong';
            jsx = <></>
    }

    return (
        <Modal show={show} onHide={handleModalCancel} backdrop='static'>
            <Modal.Header closeButton>{title}</Modal.Header>
            <Modal.Body>
                {jsx}
            </Modal.Body>
            <Modal.Footer className='ss-details-footer'>
                <Button variant='secondary' onClick={clearForm}>Clear form</Button>
                <div>
                    <Button className='purple-btn' onClick={handleSubmit}>Ok</Button>
                    <Button variant='secondary' onClick={handleModalCancel}>Cancel</Button>
                </div>
            </Modal.Footer>
        </Modal>
    )
}